import { useState } from "react";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { 
  LayoutDashboard, 
  Users, 
  Ticket, 
  Store, 
  DollarSign,
  Menu,
  X,
  FileText,
  LogOut,
  ChevronRight,
  Briefcase,
  Mail,
  HeartHandshake,
  UserPlus,
  Search,
  ShieldCheck,
  Gift,
  Contact
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { KeyRound } from "lucide-react";

const ALL_ROLES = ["admin", "staff", "referral_partner"];
const navigation = [
  { name: "Submit Referral", href: "/referrals/new", icon: UserPlus, roles: ["referral_partner"] },
  { name: "My Referrals", href: "/referrals/mine", icon: Search, roles: ["referral_partner"] },
  { name: "Dashboard", href: "/dashboard", icon: LayoutDashboard, roles: ["admin", "staff"] },
  { name: "Beneficiaries", href: "/beneficiaries", icon: Users, roles: ["admin", "staff"] },
  { name: "Applications", href: "/applications", icon: HeartHandshake, roles: ["admin", "staff"] },
  { name: "Vouchers", href: "/vouchers", icon: Ticket, roles: ["admin", "staff"] },
  { name: "Partners", href: "/partners", icon: Store, roles: ["admin", "staff"] },
  { name: "Budget", href: "/budget", icon: DollarSign, roles: ["admin"] },
  { name: "Grants", href: "/grants", icon: Briefcase, roles: ["admin"] },
  { name: "Donations", href: "/donations", icon: Gift, roles: ["admin"] },
  { name: "Community Contacts", href: "/contacts", icon: Contact, roles: ["admin"] },
  { name: "Email Templates", href: "/outreach", icon: Mail, roles: ["admin", "staff"] },
  { name: "Users", href: "/users", icon: ShieldCheck, roles: ["admin"] },
];

export const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const role = user?.role || "admin";
  const visibleNav = navigation.filter((item) => item.roles.includes(role));

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-slate-900/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={cn(
          "fixed left-0 top-0 z-50 h-screen w-64 bg-white border-r border-slate-200 transform transition-transform duration-300 lg:translate-x-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-6 border-b border-slate-200">
          <NavLink to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-slate-900 to-amber-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">SV</span>
            </div>
            <span className="font-semibold text-slate-900 text-sm">Soar Your Visibility</span>
          </NavLink>
          <button 
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-slate-500 hover:text-slate-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1">
          {visibleNav.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <NavLink
                key={item.name}
                to={item.href}
                data-testid={`nav-${item.name.toLowerCase().replace(/ /g, '-')}`}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                  isActive 
                    ? "bg-slate-900 text-white" 
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                )}
              >
                <item.icon className="w-5 h-5" />
                {item.name}
                {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
              </NavLink>
            );
          })}
        </nav>

        {/* Bottom section */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200">
          {(role === "admin" || role === "staff") && (
            <NavLink 
              to="/business-plan"
              data-testid="nav-business-plan"
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors"
            >
              <FileText className="w-5 h-5" />
              Business Plan
            </NavLink>
          )}
          <button 
            onClick={() => setPwOpen(true)}
            data-testid="nav-change-password"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors"
          >
            <KeyRound className="w-5 h-5" />
            Change Password
          </button>
          <button 
            onClick={handleLogout}
            data-testid="nav-logout"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors mt-1"
          >
            <LogOut className="w-5 h-5" />
            Sign Out
          </button>
        </div>
      </aside>
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top header */}
        <header className="sticky top-0 z-30 h-16 bg-white/80 backdrop-blur-xl border-b border-slate-200 flex items-center justify-between px-6">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden text-slate-600 hover:text-slate-900"
            data-testid="mobile-menu-toggle"
          >
            <Menu className="w-6 h-6" />
          </button>
          
          <div className="flex items-center gap-4 ml-auto">
            <div className="text-right hidden sm:block">
              <div className="text-sm font-medium text-slate-700" data-testid="header-user-name">{user?.name || "User"}</div>
              <div className="text-xs text-slate-400 capitalize">{(user?.role || "").replace("_", " ")}</div>
            </div>
            <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
              <span className="text-amber-700 font-medium text-sm">{(user?.name || "U").charAt(0).toUpperCase()}</span>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;
